// 遠端協助的 API（SPEC #kinio-kms-api；kmgr fc3f36e internal/api/remote.go）：都走 kmgr token（../api.ts kmgrFetch），mock 開關同 KMS 操作台。
//
//   POST /v1/homes/{home}/kmgr/things/{device}/remote-sessions {mode, permissions, reason?, assist_request_id?} → {session_id, ticket_hash, expires_at}
//   POST …/{session}/start {receipt}                 → {ticket, launch_url, state}（kmgr 先驗收據才送 session_open）
//   POST …/{session}/upgrade {add_permissions?, extend?}（5 分鐘內 passkey step-up）
//   POST …/{session}/end {note?, external_ticket?}   → remote_session（已結束的再送 = 補備註）
//   GET  …/remote-sessions?cursor=&limit=            → {remote_sessions, next_cursor}（這台的紀錄）
//   GET  …/{session}                                  → {remote_session, events}
//   GET  /v1/orgs/{org}/kmgr/assist-requests          → {assist_requests}（開著的）
//
//   POST …/{session}/launch                          → {launch_url, state}（解鎖後才跳同意視窗、或 120 秒過了；M4-06b）
//   GET  /v1/me/kmgr/remote-sessions?scope=&from=&to=&cursor=&limit= → {remote_sessions, next_cursor, access, names}（跨家戶紀錄；M4-06b）
import { kmgrFetch } from '../api';
import type { HostRow } from '../types';
import type { AssistRequest, RecordsQuery, RecordsResult, RemoteCreated, RemoteCreateInput, RemoteEvent, RemotePerm, RemoteSession, RemoteSessionDetail, RemoteStarted } from './types';

const enc = encodeURIComponent;
const dev = (home: string, device: string) => `/v1/homes/${enc(home)}/kmgr/things/${enc(device)}/remote-sessions`;

interface WireDetail {
  remote_session: RemoteSession;
  events?: RemoteEvent[];
}

interface WireList {
  remote_sessions?: RemoteSession[];
  next_cursor?: string;
  access?: RecordsResult['access'];
  names?: {
    users?: Record<string, string>;
    things?: Record<string, string>;
    homes?: Record<string, string>;
    orgs?: Record<string, string>;
  };
}

export const remoteApi = {
  create: (api: string, home: string, device: string, body: RemoteCreateInput) => kmgrFetch<RemoteCreated>(api, dev(home, device), { method: 'POST', body }),
  start: (api: string, home: string, device: string, session: string, receipt: string) => kmgrFetch<RemoteStarted>(api, `${dev(home, device)}/${enc(session)}/start`, { method: 'POST', body: { receipt } }),
  get: async (api: string, home: string, device: string, session: string): Promise<RemoteSessionDetail> => {
    const w = await kmgrFetch<WireDetail>(api, `${dev(home, device)}/${enc(session)}`);
    return { session: w.remote_session, events: w.events ?? [] };
  },
  upgrade: (api: string, home: string, device: string, session: string, body: { add_permissions?: RemotePerm[]; extend?: boolean }) =>
    kmgrFetch<{ session_id: string; rev: number; state: string }>(api, `${dev(home, device)}/${enc(session)}/upgrade`, { method: 'POST', body }),
  relaunch: (api: string, home: string, device: string, session: string) => kmgrFetch<RemoteStarted>(api, `${dev(home, device)}/${enc(session)}/launch`, { method: 'POST' }),
  end: (api: string, home: string, device: string, session: string, body: { note?: string; external_ticket?: string } = {}) =>
    kmgrFetch<RemoteSession>(api, `${dev(home, device)}/${enc(session)}/end`, {
      method: 'POST',
      body,
    }),
  listDevice: async (api: string, home: string, device: string, cursor?: string, limit = 50): Promise<{ sessions: RemoteSession[]; next_cursor?: string }> => {
    const w = await kmgrFetch<WireList>(api, dev(home, device), {
      query: { cursor, limit },
    });
    return {
      sessions: w.remote_sessions ?? [],
      next_cursor: w.next_cursor || undefined,
    };
  },
  assistRequests: async (api: string, org: string): Promise<AssistRequest[]> => {
    const w = await kmgrFetch<{
      assist_requests?: Omit<AssistRequest, 'org_id'>[];
    }>(api, `/v1/orgs/${enc(org)}/kmgr/assist-requests`);
    return (w.assist_requests ?? []).map((a) => ({ ...a, org_id: org }));
  },
};

/**
 * 紀錄清單（期間內、範圍內）：kmgr 的跨主機清單 GET /v1/me/kmgr/remote-sessions（M4-06b）。
 * access：full = 範圍內每個家戶都看得到全部；partial = 有些家戶只看得到自己發起的；own = 全部只看得到自己發起的。
 * names：kmgr 有回就用；沒有的操作者名稱由畫面用成員清單補；主機、家戶名稱用範圍內的主機清單補。
 */
export async function loadRecords(api: string, q: RecordsQuery, hosts: HostRow[]): Promise<RecordsResult> {
  const byRef = new Map(hosts.map((h) => [h.target_ref, h]));
  const out: RemoteSession[] = [];
  let access: RecordsResult['access'] = 'full';
  let cursor: string | undefined;
  for (let page = 0; page < 20; page++) {
    const w = await kmgrFetch<WireList>(api, '/v1/me/kmgr/remote-sessions', {
      query: { scope: q.scope, from: q.from, to: q.to, cursor, limit: 200 },
    });
    if (w.access) access = w.access;
    const n = w.names ?? {};
    for (const s of w.remote_sessions ?? [])
      out.push(
        withHost(
          {
            ...s,
            operator_name: s.operator_name ?? n.users?.[s.operator],
            host_name: s.host_name ?? n.things?.[s.device_ref],
            home_name: s.home_name ?? n.homes?.[s.home_id],
            org_name: s.org_name ?? (s.org_id ? n.orgs?.[s.org_id] : undefined),
          },
          byRef.get(s.device_ref),
        ),
      );
    if (!w.next_cursor) break;
    cursor = w.next_cursor;
  }
  return { sessions: sortRecords(out), access };
}

function withHost(s: RemoteSession, h: HostRow | undefined): RemoteSession {
  if (!h) return s;
  return {
    ...s,
    host_name: s.host_name ?? h.hostname,
    home_name: s.home_name ?? h.home_name,
    org_id: s.org_id ?? h.org_id,
    org_name: s.org_name ?? h.org_name,
  };
}

/** 由新到舊。 */
export function sortRecords(list: RemoteSession[]): RemoteSession[] {
  return [...list].sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at));
}
